<template>
  <div class="min-h-screen bg-gray-50">
    <section class="mx-auto max-w-5xl px-4 py-10 lg:px-8">
      <div class="mb-8 space-y-2 text-center">
        <p class="text-xs font-semibold uppercase tracking-[0.2em] text-brand">Join Invi8</p>
        <h1 class="text-3xl font-semibold text-ink">Vendor & Venue Onboarding</h1>
        <p class="text-sm text-muted">Complete your business profile in three quick steps.</p>
      </div>

      <div class="mb-6 flex flex-wrap items-center justify-center gap-3">
        <div
          v-for="(step, index) in steps"
          :key="step.key"
          class="flex items-center gap-2 text-sm font-semibold"
        >
          <span
            class="flex h-8 w-8 items-center justify-center rounded-full border"
            :class="stepIndex === index ? 'border-brand bg-brand text-white' : 'border-gray-300 bg-white text-ink'"
          >
            {{ index + 1 }}
          </span>
          <span :class="stepIndex === index ? 'text-ink' : 'text-muted'">{{ step.label }}</span>
          <span v-if="index < steps.length - 1" class="mx-2 hidden h-px w-10 bg-gray-300 sm:block"></span>
        </div>
      </div>

      <form class="space-y-6 rounded-2xl bg-white p-6 shadow-card ring-1 ring-gray-100" @submit.prevent>
        <div v-if="stepIndex === 0" class="space-y-4">
          <div>
            <h2 class="text-lg font-semibold text-ink">Business Information</h2>
            <p class="text-xs text-muted">Enter your business information.</p>
          </div>

          <label class="space-y-1 text-sm font-semibold text-ink">
            Business name
            <input v-model.trim="form.businessName" type="text" class="form-input" :class="inputError('businessName')" placeholder="Business Name">
            <span v-if="errors.businessName" class="error-text">{{ errors.businessName }}</span>
          </label>

          <label class="space-y-1 text-sm font-semibold text-ink">
            Listing type
            <select v-model="form.listingType" class="form-input" :class="inputError('listingType')">
              <option value="">Choose Listing Type</option>
              <option value="govt">Govt agencies</option>
              <option value="private">Private</option>
              <option value="freelancer">Freelancer</option>
              <option value="organization">Organization</option>
            </select>
            <span v-if="errors.listingType" class="error-text">{{ errors.listingType }}</span>
          </label>

          <label class="space-y-1 text-sm font-semibold text-ink">
            Business category
            <select v-model="form.businessCategory" class="form-input" :class="inputError('businessCategory')">
              <option value="">Choose Category</option>
              <option v-for="cat in categoryOptions" :key="cat" :value="cat">{{ cat }}</option>
            </select>
            <span v-if="errors.businessCategory" class="error-text">{{ errors.businessCategory }}</span>
          </label>

          <label class="space-y-1 text-sm font-semibold text-ink">
            Business type
            <select v-model="form.businessType" class="form-input" :class="inputError('businessType')">
              <option value="">Choose Business Type</option>
              <option value="vendor">Vendor</option>
              <option value="venue">Venue</option>
              <option value="both">Vendor + Venue</option>
            </select>
            <span v-if="errors.businessType" class="error-text">{{ errors.businessType }}</span>
          </label>
        </div>

        <div v-else-if="stepIndex === 1" class="space-y-4">
          <div>
            <h2 class="text-lg font-semibold text-ink">Personal Information</h2>
            <p class="text-xs text-muted">Enter your personal information.</p>
          </div>

          <label class="space-y-1 text-sm font-semibold text-ink">
            Phone number
            <input
              ref="phoneInputRef"
              type="tel"
              class="form-input"
              :class="inputError('phone')"
              placeholder="202 555 0111"
              @input="syncPhone"
              @blur="syncPhone"
            >
            <span v-if="errors.phone" class="error-text">{{ errors.phone }}</span>
          </label>

          <label class="space-y-1 text-sm font-semibold text-ink">
            Email
            <input v-model.trim="form.email" type="email" class="form-input" :class="inputError('email')" placeholder="Email">
            <span v-if="errors.email" class="error-text">{{ errors.email }}</span>
          </label>

          <label class="space-y-1 text-sm font-semibold text-ink">
            Username
            <input v-model.trim="form.username" type="text" class="form-input" :class="inputError('username')" placeholder="Username">
            <span v-if="errors.username" class="error-text">{{ errors.username }}</span>
          </label>

          <div class="grid gap-4 sm:grid-cols-2">
            <label class="space-y-1 text-sm font-semibold text-ink">
              Password
              <div class="relative">
                <input v-model="form.password" :type="showPassword ? 'text' : 'password'" class="form-input pr-10" :class="inputError('password')" placeholder="Password">
                <button type="button" class="toggle-btn" @click="showPassword = !showPassword">
                  <i :class="showPassword ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
                </button>
              </div>
              <span v-if="errors.password" class="error-text">{{ errors.password }}</span>
            </label>
            <label class="space-y-1 text-sm font-semibold text-ink">
              Confirm password
              <div class="relative">
                <input v-model="form.confirmPassword" :type="showConfirmPassword ? 'text' : 'password'" class="form-input pr-10" :class="inputError('confirmPassword')" placeholder="Confirm Password">
                <button type="button" class="toggle-btn" @click="showConfirmPassword = !showConfirmPassword">
                  <i :class="showConfirmPassword ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
                </button>
              </div>
              <span v-if="errors.confirmPassword" class="error-text">{{ errors.confirmPassword }}</span>
            </label>
          </div>
        </div>

        <div v-else class="space-y-4">
          <div>
            <h2 class="text-lg font-semibold text-ink">Available Business</h2>
            <p class="text-xs text-muted">Choose an option to proceed.</p>
          </div>

          <div class="grid gap-4 md:grid-cols-2">
            <label class="radio-card" :class="form.visibility === 'public' ? 'radio-active' : ''">
              <input v-model="form.visibility" type="radio" value="public" class="sr-only">
              <div>
                <p class="font-semibold">Public</p>
                <p class="text-xs text-muted">Your business will be visible to public</p>
              </div>
            </label>
            <label class="radio-card" :class="form.visibility === 'private' ? 'radio-active' : ''">
              <input v-model="form.visibility" type="radio" value="private" class="sr-only">
              <div>
                <p class="font-semibold">Private</p>
                <p class="text-xs text-muted">Your business will be private</p>
              </div>
            </label>
          </div>

          <div>
            <h3 class="text-sm font-semibold text-ink">Are you at business location?</h3>
            <p class="text-xs text-muted">Search or drag drop at your business location.</p>
          </div>

          <div class="grid gap-4 md:grid-cols-2">
            <label class="radio-card" :class="form.locationMode === 'current' ? 'radio-active' : ''">
              <input v-model="form.locationMode" type="radio" value="current" class="sr-only">
              <div>
                <p class="font-semibold">Current location</p>
                <p class="text-xs text-muted">Are you at your business location?</p>
              </div>
            </label>
            <label class="radio-card" :class="form.locationMode === 'other' ? 'radio-active' : ''">
              <input v-model="form.locationMode" type="radio" value="other" class="sr-only">
              <div>
                <p class="font-semibold">Other location</p>
                <p class="text-xs text-muted">Are you not at your business location?</p>
              </div>
            </label>
          </div>

          <div class="space-y-3 rounded-2xl border border-gray-100 bg-white p-4">
            <div class="flex flex-wrap items-center justify-between gap-3">
              <button type="button" class="btn-secondary" @click="handleCurrentLocation">Get current location</button>
              <div class="flex w-full items-center gap-2 md:w-auto">
                <input v-model.trim="form.locationQuery" type="text" class="form-input" placeholder="Search for location / address">
                <button type="button" class="btn-primary" @click="handleSearch">Search</button>
              </div>
            </div>
            <div id="onboard-map" class="h-[320px] w-full overflow-hidden rounded-2xl border border-gray-100"></div>
            <span v-if="errors.location" class="error-text">{{ errors.location }}</span>
          </div>

          <label class="space-y-1 text-sm font-semibold text-ink">
            Referral code
            <input v-model.trim="form.referralCode" type="text" class="form-input" :class="inputError('referralCode')" placeholder="Referral Code">
            <span v-if="errors.referralCode" class="error-text">{{ errors.referralCode }}</span>
          </label>

          <label class="flex items-start gap-3 text-sm font-semibold text-ink">
            <input v-model="form.terms" type="checkbox" class="mt-1 h-4 w-4 rounded border-gray-300 text-brand">
            <span>
              Agree to our <RouterLink class="text-brand" to="/terms">Terms & Conditions</RouterLink>
              <span v-if="errors.terms" class="error-text block">{{ errors.terms }}</span>
            </span>
          </label>
        </div>

        <div class="flex flex-wrap items-center justify-between gap-3">
          <div class="text-sm text-muted">Step {{ stepIndex + 1 }} of {{ steps.length }}</div>
          <div class="flex gap-3">
            <button
              type="button"
              class="rounded-full border border-gray-300 px-4 py-2 text-sm font-semibold text-ink transition hover:border-brand hover:text-brand"
              :disabled="stepIndex === 0"
              :class="{ 'opacity-50 cursor-not-allowed': stepIndex === 0 }"
              @click="prevStep"
            >
              Back
            </button>
            <button
              v-if="stepIndex < steps.length - 1"
              type="button"
              class="rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-white shadow-card transition hover:bg-brandDark"
              @click="nextStep"
            >
              Next
            </button>
            <button
              v-else
              type="button"
              class="rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white shadow-card transition hover:bg-emerald-700"
              @click="submit"
            >
              Submit
            </button>
          </div>
        </div>
      </form>
    </section>
  </div>
</template>

<script setup>
import { reactive, ref, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { RouterLink } from 'vue-router'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import intlTelInput from 'intl-tel-input'
import 'intl-tel-input/build/css/intlTelInput.css'

const steps = [
  { key: 'business', label: 'Business' },
  { key: 'personal', label: 'Personal' },
  { key: 'location', label: 'Location' }
]

const stepIndex = ref(0)
const showPassword = ref(false)
const showConfirmPassword = ref(false)
const phoneInputRef = ref(null)
const phoneIti = ref(null)

const form = reactive({
  businessName: '',
  listingType: '',
  businessCategory: '',
  businessType: '',
  phone: '',
  phoneRaw: '',
  email: '',
  username: '',
  password: '',
  confirmPassword: '',
  visibility: 'public',
  locationMode: 'current',
  locationQuery: '',
  locationAddress: '',
  locationLat: null,
  locationLng: null,
  referralCode: '',
  terms: false
})

const categoryOptions = ['Vaasthu', 'Catering', 'Decor', 'Photography', 'Planning', 'AV/Lighting', 'Cakes', 'Entertainment', 'Transportation']
const errors = reactive({})

const inputError = (key) => (errors[key] ? 'input-error' : '')

const validateStep = (index) => {
  Object.keys(errors).forEach((key) => delete errors[key])

  if (index === 0) {
    if (!form.businessName) errors.businessName = 'The Business Name is required'
    if (!form.listingType) errors.listingType = 'The Listing Type is required'
    if (!form.businessCategory) errors.businessCategory = 'The Business Category is required'
    if (!form.businessType) errors.businessType = 'The Business Type is required'
  }

  if (index === 1) {
    if (!form.phoneRaw) errors.phone = 'The Phone Number is required'
    if (form.phoneRaw && phoneIti.value && !phoneIti.value.isValidNumber()) {
      errors.phone = 'Enter a valid phone number'
    }
    if (!form.email) errors.email = 'The Email is required'
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errors.email = 'Enter a valid email'
    if (!form.username) errors.username = 'The Username is required'
    if (!form.password) errors.password = 'The Password is required'
    if (form.password && form.password.length < 6) errors.password = 'Password must be at least 6 characters'
    if (!form.confirmPassword) errors.confirmPassword = 'Confirm Password is required'
    if (form.password && form.confirmPassword && form.password !== form.confirmPassword) errors.confirmPassword = 'Passwords do not match'
  }

  if (index === 2) {
    if (!form.locationLat || !form.locationLng) errors.location = 'Please set your business location on the map'
    if (!form.referralCode) errors.referralCode = 'Referral Code is required'
    if (!form.terms) errors.terms = 'You must agree to the Terms & Conditions'
  }

  return Object.keys(errors).length === 0
}

const nextStep = () => {
  if (!validateStep(stepIndex.value)) return
  if (stepIndex.value < steps.length - 1) stepIndex.value += 1
}

const prevStep = () => {
  if (stepIndex.value > 0) stepIndex.value -= 1
}

const submit = () => {
  if (!validateStep(stepIndex.value)) return
  // Placeholder submission handling
  // eslint-disable-next-line no-console
  console.log('Vendor onboarding data', { ...form })
}

const syncPhone = () => {
  if (!phoneInputRef.value) return
  form.phoneRaw = phoneInputRef.value.value || ''
  if (phoneIti.value && phoneIti.value.isValidNumber()) {
    form.phone = phoneIti.value.getNumber()
  } else {
    form.phone = ''
  }
}

const initPhoneInput = () => {
  if (!phoneInputRef.value || phoneIti.value) return
  phoneIti.value = intlTelInput(phoneInputRef.value, {
    initialCountry: 'us',
    separateDialCode: true,
    nationalMode: false,
    preferredCountries: ['us', 'in', 'gb', 'au'],
    autoPlaceholder: 'polite'
  })
  if (form.phone) {
    phoneIti.value.setNumber(form.phone)
  }
  syncPhone()
}

const mapRef = ref(null)
const markerRef = ref(null)

const ensureMap = async () => {
  await nextTick()
  if (mapRef.value) return
  mapRef.value = L.map('onboard-map').setView([20.5937, 78.9629], 5)
  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '&copy; OpenStreetMap'
  }).addTo(mapRef.value)
  mapRef.value.on('click', (event) => {
    setMarker(event.latlng.lat, event.latlng.lng, 'Pinned location')
  })
}

const setMarker = (lat, lng, address) => {
  if (!mapRef.value) return
  if (!markerRef.value) {
    markerRef.value = L.marker([lat, lng], { draggable: true }).addTo(mapRef.value)
    markerRef.value.on('dragend', () => {
      const pos = markerRef.value.getLatLng()
      setMarker(pos.lat, pos.lng, 'Pinned location')
    })
  } else {
    markerRef.value.setLatLng([lat, lng])
  }
  mapRef.value.setView([lat, lng], 14)
  form.locationLat = lat
  form.locationLng = lng
  form.locationAddress = address || form.locationAddress
}

const handleCurrentLocation = () => {
  if (!navigator.geolocation) return
  navigator.geolocation.getCurrentPosition((pos) => {
    const lat = pos.coords.latitude
    const lng = pos.coords.longitude
    setMarker(lat, lng, 'Current location')
  })
}

const handleSearch = async () => {
  if (!form.locationQuery) return
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(form.locationQuery)}`
    )
    const results = await response.json()
    if (!results?.length) return
    const place = results[0]
    const lat = Number(place.lat)
    const lng = Number(place.lon)
    setMarker(lat, lng, place.display_name)
  } catch {
    // ignore search failures
  }
}

watch(stepIndex, (val) => {
  if (val === 2) {
    ensureMap().then(() => {
      if (mapRef.value) mapRef.value.invalidateSize()
    })
  }
})

onMounted(() => {
  initPhoneInput()
})

onUnmounted(() => {
  if (phoneIti.value) {
    phoneIti.value.destroy()
    phoneIti.value = null
  }
})
</script>

<style scoped>
.form-input {
  width: 100%;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  padding: 10px 12px;
  font-size: 14px;
  color: #0f172a;
  background: #fff;
}

.form-input:focus {
  outline: none;
  border-color: #ec2263;
  box-shadow: 0 0 0 3px rgba(236, 34, 99, 0.15);
}

.input-error {
  border-color: #ef4444;
}

.error-text {
  display: block;
  font-size: 12px;
  color: #ef4444;
  font-weight: 600;
}

.radio-card {
  display: flex;
  align-items: center;
  gap: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 14px;
  cursor: pointer;
  background: #fff;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.radio-active {
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

.btn-secondary {
  border-radius: 999px;
  border: 1px solid #d1d5db;
  padding: 8px 16px;
  font-size: 12px;
  font-weight: 600;
  color: #111827;
  background: #fff;
}

.btn-primary {
  border-radius: 999px;
  border: 1px solid #6366f1;
  padding: 8px 16px;
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  background: #6366f1;
}

.toggle-btn {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
}

:deep(.iti) {
  width: 100%;
}

:deep(.iti__country-list) {
  z-index: 50;
}
</style>
